import { isLeft } from "fp-ts/lib/Either";
import fetchApi, { getServiceUrl } from "ui-io/fetch-api";
import {
  AssetOptions,
  AssetPropertiesIO,
  AssetPropertiesT,
} from "../../assets-io";
import formatIoTsErrors from "../../format-io-ts-errors";
import { AssetReferenceT } from "../../task-io";

const getQueryString = (options?: AssetOptions) =>
  [
    ["compressed", String(options?.compressed ?? false)],
    ["ignore_expiry", String(options?.ignore_expiry ?? true)],
    ["attachment", String(options?.attachment ?? true)],
  ].reduce(
    (queryString, [key, value]) =>
      `${queryString}&${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    ""
  );

const getAssetUrls = async (asset: AssetReferenceT) => {
  if (!asset.name && !asset.fileUrl) {
    throw new Error("Cannot fetch asset without an name or url");
  }
  const service = "admin-service";
  const pathname = `/get-file/${asset.name}`;
  // const environment = asset.environment;
  const environment = process.env.REACT_APP_ENVIRONMENT || "development";

  const serviceUrl = await getServiceUrl({
    service,
    pathname,
    environment,
  });
  if (!serviceUrl) {
    throw new Error(
      `Cannot determine url for ${service}${
        environment ? `for environment "${environment}"` : ""
      }`
    );
  }
  const getAssetsUrl = (options?: AssetOptions) => {
    let assetsUrl = "";
    if (asset.fileUrl) {
      assetsUrl = `${asset.fileUrl}?${getQueryString(options)}`;
    } else if (asset.name) {
      assetsUrl = `${serviceUrl}?${getQueryString(options)}`;
    }
    return assetsUrl;
  };

  return {
    getAssetUrlQuery: (options?: AssetOptions) => getAssetsUrl(options),
    assetPropertiesUrl: `${serviceUrl}/properties?ignore_expiry=true`,
  };
};

export const getAssetInfoQuery = (
  asset: AssetReferenceT,
  options: AssetOptions
) => ({
  queryKey: ["assetInfo", JSON.stringify(asset), JSON.stringify(options)],
  queryFn: async () => {
    const { assetPropertiesUrl, getAssetUrlQuery } = await getAssetUrls(asset);
    let assetProperties: AssetPropertiesT | undefined;
    const res = await fetch(assetPropertiesUrl);
    const response = await res.json();
    if (response.responseCode >= 400) {
      throw new Error(`Network error: ${response.responseCode}`);
    } else {
      const decodedResponseBody = AssetPropertiesIO.decode(
        response.responseData
      );
      if (isLeft(decodedResponseBody)) {
        const message = [
          "Encoding failed: API Returned invalid response.",
          formatIoTsErrors("object", decodedResponseBody.left),
        ].join("\n");
        throw new Error(message);
      } else {
        assetProperties = decodedResponseBody.right;
      }
    }

    return Promise.resolve({
      assetProperties,
      getAssetUrlQuery,
    });
  },
});
